import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const DIALPAD_ALLOWED_ORIGINS = [
  'https://dialpad.com',
  'https://www.dialpad.com',
];

type DialpadCommandMethod =
  | 'initiate_call'
  | 'enable_current_tab'
  | 'hang_up_all_calls';

export type DialpadStatus = 'disabled' | 'loading' | 'ready' | 'error';

export interface DialpadCallOptions {
  phoneNumber: string;
  enableCurrentTab?: boolean;
  identityType?: 'Office' | 'OfficeGroup' | 'CallCenter';
  identityId?: string | number;
  outboundCallerId?: string;
  customData?: Record<string, unknown> | string;
}

export interface DialpadCallRingingPayload {
  state: 'on' | 'off';
  id: number;
  contact?: {
    id?: string;
    phone?: string;
    type?: string;
    email?: string;
    name?: string;
  };
  target?: {
    id?: string;
    phone?: string;
    type?: string;
    email?: string;
    name?: string;
  };
  internal_number?: string;
  external_number?: string;
  custom_data?: string;
}

interface DialpadCtiContextValue {
  status: DialpadStatus;
  isEnabled: boolean;
  isAuthenticated: boolean;
  iframeUrl: string | null;
  errorMessage: string | null;
  lastAuthenticationChange: number | null;
  lastCallRingingEvent: DialpadCallRingingPayload | null;
  isPanelOpen: boolean;
  setPanelOpen: (open: boolean) => void;
  openPanel: () => void;
  closePanel: () => void;
  registerIframe: (node: HTMLIFrameElement | null) => void;
  markIframeReady: () => void;
  initiateCall: (options: DialpadCallOptions) => boolean;
  enableCurrentTab: () => boolean;
  hangUpAll: () => boolean;
}

const DialpadCtiContext = createContext<DialpadCtiContextValue | null>(null);

export const DialpadCtiProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [iframeReady, setIframeReady] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [lastAuthChange, setLastAuthChange] = useState<number | null>(null);
  const [ringingEvent, setRingingEvent] =
    useState<DialpadCallRingingPayload | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const isFeatureEnabled = useMemo(() => {
    const raw = (import.meta.env.VITE_APP_ENABLE_DIALPAD_MINI_DIALER || '')
      .toString()
      .trim()
      .toLowerCase();
    return raw === 'true' || raw === '1';
  }, []);

  const clientId =
    import.meta.env.VITE_APP_DIALPAD_CLIENT_ID?.toString().trim();
  const iframeUrl = clientId ? `https://dialpad.com/apps/${clientId}` : null;
  const isEnabled = Boolean(isFeatureEnabled && iframeUrl);

  useEffect(() => {
    if (isFeatureEnabled && !clientId && !errorMessage) {
      setErrorMessage('Dialpad client ID missing');
    }
  }, [clientId, errorMessage, isFeatureEnabled]);

  const status: DialpadStatus = useMemo(() => {
    if (!isEnabled) return 'disabled';
    if (errorMessage) return 'error';
    if (!iframeReady) return 'loading';
    return 'ready';
  }, [errorMessage, iframeReady, isEnabled]);

  const registerIframe = useCallback((node: HTMLIFrameElement | null) => {
    iframeRef.current = node;
    if (!node) {
      setIframeReady(false);
    }
  }, []);

  const markIframeReady = useCallback(() => {
    if (!iframeReady) {
      setIframeReady(true);
    }
  }, [iframeReady]);

  const openPanel = useCallback(() => {
    setIsPanelOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setIsPanelOpen(false);
  }, []);

  const setPanelOpen = useCallback((open: boolean) => {
    setIsPanelOpen(open);
  }, []);

  const postMessage = useCallback(
    (method: DialpadCommandMethod, payload?: Record<string, unknown>) => {
      if (!iframeRef.current || !isEnabled) return false;
      const targetWindow = iframeRef.current.contentWindow;
      if (!targetWindow) return false;
      targetWindow.postMessage(
        {
          api: 'opencti_dialpad',
          version: '1.0',
          method,
          payload,
        },
        DIALPAD_ALLOWED_ORIGINS[0],
      );
      return true;
    },
    [isEnabled],
  );

  const initiateCall = useCallback(
    (options: DialpadCallOptions) => {
      if (!options.phoneNumber) return false;
      const payload: Record<string, unknown> = {
        phone_number: options.phoneNumber,
      };
      if (options.enableCurrentTab !== undefined) {
        payload.enable_current_tab = options.enableCurrentTab;
      }
      if (options.identityType) {
        payload.identity_type = options.identityType;
      }
      if (options.identityId) {
        payload.identity_id = options.identityId;
      }
      if (options.outboundCallerId) {
        payload.outbound_caller_id = options.outboundCallerId;
      }
      if (options.customData) {
        payload.custom_data =
          typeof options.customData === 'string'
            ? options.customData
            : JSON.stringify(options.customData);
      }
      return postMessage('initiate_call', payload);
    },
    [postMessage],
  );

  const enableCurrentTab = useCallback(() => {
    return postMessage('enable_current_tab');
  }, [postMessage]);

  const hangUpAll = useCallback(() => {
    return postMessage('hang_up_all_calls');
  }, [postMessage]);

  useEffect(() => {
    if (!isEnabled) return undefined;

    const handleMessage = (event: MessageEvent) => {
      const { origin, data } = event;
      if (!DIALPAD_ALLOWED_ORIGINS.includes(origin)) return;
      if (!data || data.api !== 'opencti_dialpad') return;

      if (data.method === 'user_authentication') {
        const { user_authenticated } = data.payload || {};
        setIsAuthenticated(Boolean(user_authenticated));
        setLastAuthChange(Date.now());
      }

      if (data.method === 'call_ringing') {
        setRingingEvent(data.payload as DialpadCallRingingPayload);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isEnabled]);

  useEffect(() => {
    if (!isEnabled) return undefined;

    const handleFocus = () => {
      enableCurrentTab();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [enableCurrentTab, isEnabled]);

  const value = useMemo<DialpadCtiContextValue>(
    () => ({
      status,
      isEnabled,
      isAuthenticated,
      iframeUrl,
      errorMessage,
      lastAuthenticationChange: lastAuthChange,
      lastCallRingingEvent: ringingEvent,
      isPanelOpen,
      setPanelOpen,
      openPanel,
      closePanel,
      registerIframe,
      markIframeReady,
      initiateCall,
      enableCurrentTab,
      hangUpAll,
    }),
    [
      closePanel,
      enableCurrentTab,
      hangUpAll,
      iframeUrl,
      isPanelOpen,
      initiateCall,
      isAuthenticated,
      isEnabled,
      errorMessage,
      lastAuthChange,
      openPanel,
      ringingEvent,
      setPanelOpen,
      status,
      registerIframe,
      markIframeReady,
    ],
  );

  return (
    <DialpadCtiContext.Provider value={value}>
      {children}
    </DialpadCtiContext.Provider>
  );
};

export const useDialpadCti = () => {
  const ctx = useContext(DialpadCtiContext);
  if (!ctx) {
    throw new Error('useDialpadCti must be used within DialpadCtiProvider');
  }
  return ctx;
};
